'use client';

import { useState } from 'react';

import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp, Minus, Plus, Search } from 'lucide-react';

import {
  type AddonSizeOption,
  AddonSizePicker,
} from '@kit/booking-addons/components/addon-size-picker';
import {
  groupAddonsBySize,
  sizeOfAddonRow,
} from '@kit/bookings/utils/addon-size-groups';
import { CurrencyIcon } from '@kit/ui/currency-icon';
import { Button } from '@kit/ui/button';
import { CardContent } from '@kit/ui/card';
import { Checkbox } from '@kit/ui/checkbox';
import { Input } from '@kit/ui/input';

import type { SelectedAddon } from '../_lib/hooks/use-booking-reducer';
import { BookingSection } from './booking-presentation';

interface CustomerAddon {
  id: string;
  name: string;
  price: number;
  category: string;
  variant_group_id?: string | null;
  variant_label?: string | null;
}

interface CustomerAddonSelectorProps {
  addons: CustomerAddon[];
  selectedAddons: Map<string, SelectedAddon>;
  onToggle: (addon: { id: string; name: string; price: number }) => void;
  onQuantityChange: (addonId: string, quantity: number) => void;
}

export function CustomerAddonSelector({
  addons,
  selectedAddons,
  onToggle,
  onQuantityChange,
}: CustomerAddonSelectorProps) {
  const { t } = useTranslation('addons');
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');

  const selectedCount = selectedAddons.size;
  const totalPrice = Array.from(selectedAddons.values()).reduce(
    (sum, a) => sum + a.price * a.quantity,
    0,
  );

  const filtered = search
    ? addons.filter((a) =>
        a.name.toLowerCase().includes(search.toLowerCase()),
      )
    : addons;

  const entries = groupAddonsBySize(filtered, sizeOfAddonRow);
  const quantities = new Map(
    Array.from(selectedAddons.values()).map((a) => [a.id, a.quantity]),
  );

  // Sizes reuse the existing actions: a new size is toggled in, then its
  // quantity is set; quantity 0 removes it.
  const setSizeQuantity = (
    size: AddonSizeOption & { name: string },
    quantity: number,
  ) => {
    if (!selectedAddons.has(size.id)) {
      if (quantity > 0) onToggle({ id: size.id, name: size.name, price: size.price });
      if (quantity > 1) onQuantityChange(size.id, quantity);
      return;
    }
    onQuantityChange(size.id, quantity);
  };

  return (
    <BookingSection>
      <CardContent className="p-0">
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="flex w-full items-center justify-between p-3 md:p-4"
        >
          <div className="flex flex-col items-start gap-0.5">
            <span className="text-sm font-medium">{t('addExtras')}</span>
            {selectedCount > 0 ? (
              <span className="text-muted-foreground flex items-center gap-1 text-xs">
                {t('itemsSelected', { count: selectedCount })} ·{' '}
                <CurrencyIcon className="h-2.5 w-2.5" /> {totalPrice.toFixed(2)}
              </span>
            ) : (
              <span className="text-muted-foreground text-xs">
                {t('tapToBrowseMenu')}
              </span>
            )}
          </div>

          {isOpen ? (
            <ChevronUp className="text-muted-foreground h-4 w-4" />
          ) : (
            <ChevronDown className="text-muted-foreground h-4 w-4" />
          )}
        </button>

        {isOpen && (
          <div className="border-t px-3 pb-3 md:px-4 md:pb-4">
            {addons.length > 5 && (
              <div className="relative mt-3">
                <Search className="text-muted-foreground absolute top-2.5 start-2.5 h-4 w-4" />
                <Input
                  placeholder={t('searchExtras')}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="ps-8 h-9 text-sm"
                />
              </div>
            )}

            <div className="mt-3 max-h-60 space-y-1 overflow-y-auto">
              {entries.map((entry) => {
                if (entry.kind === 'group') {
                  const sizes = entry.sizes.map((size) => ({
                    id: size.id,
                    name: size.name,
                    label: size.variant_label ?? '',
                    price: Number(size.price),
                  }));

                  return (
                    <div
                      key={entry.groupId}
                      className="space-y-2 rounded-md px-2 py-1.5"
                      data-test="customer-addon-size-group"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm">{entry.baseName}</span>
                        <span className="text-muted-foreground flex items-center gap-0.5 text-xs">
                          {t('sizes.from')} <CurrencyIcon className="h-3 w-3" />{' '}
                          {entry.minPrice.toFixed(2)}
                        </span>
                      </div>
                      <AddonSizePicker
                        productName={entry.baseName}
                        sizes={sizes}
                        quantities={quantities}
                        freeAsCheckbox
                        onChange={setSizeQuantity}
                      />
                    </div>
                  );
                }

                const { addon } = entry;
                const selected = selectedAddons.get(addon.id);

                if (addon.price === 0) {
                  return (
                    <label
                      key={addon.id}
                      className="flex min-h-11 cursor-pointer items-center justify-between gap-2 rounded-md px-2 py-1.5"
                    >
                      <span className="text-sm">{addon.name}</span>
                      <span className="flex items-center gap-2">
                        <span className="text-muted-foreground text-sm">{t('bookings:free')}</span>
                        {selected && selected.quantity > 1 && (
                          <span className="text-sm">×{selected.quantity}</span>
                        )}
                        <Checkbox
                          aria-label={addon.name}
                          checked={(selected?.quantity ?? 0) > 0}
                          onCheckedChange={(checked) => {
                            if (checked === true && !selected) onToggle(addon);
                            else onQuantityChange(addon.id, checked === true ? 1 : 0);
                          }}
                        />
                      </span>
                    </label>
                  );
                }

                return (
                  <div
                    key={addon.id}
                    className="flex items-center justify-between rounded-md px-2 py-1.5"
                  >
                    <span className="text-sm">{addon.name}</span>

                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground flex items-center gap-0.5 text-sm">
                        <CurrencyIcon className="h-3 w-3" />{' '}
                        {addon.price.toFixed(2)}
                      </span>

                      {selected ? (
                        <div className="flex items-center gap-1">
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            className="h-7 w-7"
                            aria-label={t('decreaseQuantity', { name: addon.name })}
                            onClick={() =>
                              onQuantityChange(addon.id, selected.quantity - 1)
                            }
                          >
                            <Minus className="h-3 w-3" />
                          </Button>
                          <span
                            className="w-6 text-center text-sm font-medium"
                            aria-live="polite"
                            aria-label={t('quantityLabel', { count: selected.quantity, name: addon.name })}
                          >
                            {selected.quantity}
                          </span>
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            className="h-7 w-7"
                            aria-label={t('increaseQuantity', { name: addon.name })}
                            onClick={() =>
                              onQuantityChange(addon.id, selected.quantity + 1)
                            }
                          >
                            <Plus className="h-3 w-3" />
                          </Button>
                        </div>
                      ) : (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-7 text-xs"
                          onClick={() => onToggle(addon)}
                        >
                          <Plus className="me-1 h-3 w-3" />
                          {t('addItem')}
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}

              {filtered.length === 0 && search && (
                <p className="text-muted-foreground py-4 text-center text-sm">
                  {t('noSearchResults')}
                </p>
              )}
            </div>

            {selectedCount > 0 && (
              <div className="border-t mt-2 flex items-center justify-between pt-2">
                <span className="text-muted-foreground text-sm">
                  {t('addonSubtotal')}
                </span>
                <span className="flex items-center gap-1 text-sm font-semibold">
                  +<CurrencyIcon className="h-3 w-3" /> {totalPrice.toFixed(2)}
                </span>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </BookingSection>
  );
}
